import {
	type BaseState,
	type GameDefinition,
	type MoveOutcome,
	otherPlayer,
	type PlayerIndex,
	reject,
	resultFromScores,
} from "../types";

export const SUITS = ["♥️", "♠️", "♦️", "♣️"] as const;
export const RUN_LENGTH = 9;
/** Cards face up in the middle for both players to take from. */
export const MARKET_SIZE = 5;

export interface SuitCard {
	suit: number;
	value: number;
}

export interface SuitRunState extends BaseState {
	kind: "suitrun";
	/** Face down — redacted away before sending. */
	deck: SuitCard[];
	deckLeft: number;
	/** The cards anyone may take. */
	market: (SuitCard | null)[];
	/** Each player's run, in the order they laid it. */
	runs: [SuitCard[], SuitCard[]];
	lastNote: string | null;
}

/**
 * Take a market card into your run, or swap a market card out for the top of
 * the deck. The swap is what makes a forced take a decision: it can ditch the
 * card your friend is waiting for, or churn a stale market — at the cost of
 * the point a take would have scored.
 */
export type SuitRunMove = { take: number } | { swap: number };

/**
 * A card can join a run if it carries on from either end, in the same suit.
 *
 * The run is stored in the order it was laid, but its values always form one
 * unbroken stretch — each take extends the stretch by exactly one — so the ends
 * are simply its lowest and highest values. Insisting on the upward end alone
 * left each player chasing a single card in the whole deck, and most games
 * fizzled out two moves in.
 */
export function fits(run: SuitCard[], card: SuitCard): boolean {
	if (run.length === 0) return true;
	if (card.suit !== run[0].suit) return false;
	let lowest = run[0].value;
	let highest = run[0].value;
	for (const laid of run) {
		lowest = Math.min(lowest, laid.value);
		highest = Math.max(highest, laid.value);
	}
	return card.value === highest + 1 || card.value === lowest - 1;
}

function refill(market: (SuitCard | null)[], deck: SuitCard[]): void {
	for (let slot = 0; slot < market.length; slot++) {
		if (market[slot] === null) market[slot] = deck.pop() ?? null;
	}
}

export const suitRun: GameDefinition<SuitRunState, SuitRunMove> = {
	id: "suitrun",
	category: "cards",
	title: "Suit Run",
	emoji: "🏃",
	blurb: "Build the longest run of one suit from the cards in the middle.",
	howTo: [
		"Five cards sit face up in the middle.",
		"Take one that carries your run on — same suit, one up or one down.",
		"Your first card can be anything, so choose it well.",
		"Or spend your turn swapping a middle card for a fresh one — sneaky if your friend needed it!",
		"If nobody can take a card, one gets swapped for a fresh one.",
		"When the cards run out, the longest run wins.",
	],

	create(rng) {
		const deck: SuitCard[] = [];
		for (let suit = 0; suit < SUITS.length; suit++) {
			for (let value = 1; value <= RUN_LENGTH; value++) deck.push({ suit, value });
		}
		const shuffled = rng.shuffle(deck);
		const market: (SuitCard | null)[] = shuffled.splice(0, MARKET_SIZE);

		return {
			kind: "suitrun",
			deck: shuffled,
			deckLeft: shuffled.length,
			market,
			runs: [[], []],
			lastNote: null,
			turn: 0,
			scores: [0, 0],
			result: null,
		};
	},

	parseMove(raw) {
		if (typeof raw !== "object" || raw === null) return null;
		const { take, swap } = raw as { take?: unknown; swap?: unknown };
		if (typeof take === "number" && Number.isInteger(take) && take >= 0 && take < MARKET_SIZE) {
			return { take };
		}
		if (typeof swap === "number" && Number.isInteger(swap) && swap >= 0 && swap < MARKET_SIZE) {
			return { swap };
		}
		return null;
	},

	apply(state, player, move): MoveOutcome<SuitRunState> {
		if (state.result) return reject("This game is already finished.");
		if (state.turn !== player) return reject("It's not your turn yet.");

		const deck = [...state.deck];
		const market = [...state.market];
		const runs: [SuitCard[], SuitCard[]] = [[...state.runs[0]], [...state.runs[1]]];
		let note: string;

		if ("take" in move) {
			const card = market[move.take];
			if (!card) return reject("There's no card there.");
			if (!fits(state.runs[player], card)) {
				return reject("That card doesn't carry your run on.");
			}
			market[move.take] = null;
			refill(market, deck);
			runs[player].push(card);
			note = `Took ${SUITS[card.suit]} ${card.value}.`;
		} else {
			// Swapping spends the turn on changing the market instead of scoring:
			// the old card is gone from the game, and the deck shrinks by one, so
			// a swapped game still always ends.
			const card = market[move.swap];
			if (!card) return reject("There's no card there to swap.");
			if (deck.length === 0) return reject("No cards left to swap in.");
			market[move.swap] = deck.pop() ?? null;
			note = `Swapped out ${SUITS[card.suit]} ${card.value}.`;
		}

		const scores: [number, number] = [runs[0].length, runs[1].length];

		const canTake = (seat: PlayerIndex) =>
			market.some((slot) => slot !== null && fits(runs[seat], slot));

		// The market only turns over on takes, so it can jam: five cards face
		// up that neither run can use, with most of the deck still to come.
		// Rather than call the game there, swap cards out for fresh ones — one
		// slot at a time, so useful cards aren't thrown away five at a whack —
		// until somebody can go again or the deck really has run dry.
		let swapAt = 0;
		while (!canTake(0) && !canTake(1) && deck.length > 0) {
			market[swapAt % MARKET_SIZE] = deck.pop() ?? null;
			swapAt++;
		}

		// Passing is handled here rather than on a timer: a player with nothing
		// to take is skipped, and if neither can take anything it is over.
		const them = otherPlayer(player);
		const turn = canTake(them) ? them : canTake(player) ? player : null;
		const goAgain = turn === player ? "They can't take anything — your go again!" : null;

		return {
			ok: true,
			state: {
				...state,
				deck,
				deckLeft: deck.length,
				market,
				runs,
				scores,
				lastNote: goAgain ?? note,
				turn,
				result: turn === null ? resultFromScores(scores) : null,
			},
		};
	},

	legalMoves(state, player) {
		if (state.result || state.turn !== player) return [];
		const moves: SuitRunMove[] = [];
		state.market.forEach((card, take) => {
			if (card && fits(state.runs[player], card)) moves.push({ take });
		});
		// Any face-up card can be swapped away while the deck lasts — the turn
		// spent is the price.
		if (state.deck.length > 0) {
			state.market.forEach((card, swap) => {
				if (card) moves.push({ swap });
			});
		}
		return moves;
	},

	tick() {
		return null;
	},

	nextTickAt() {
		return null;
	},

	redact(state) {
		return { ...state, deck: [] };
	},
};
